import React, { FC, useEffect, useState } from 'react';
import * as actions from '../mfaTokensActions';
import { Field } from '../types';
import { MICROSOFT_MFA_TOKEN_FIELDS } from '../../../common/fields';
import TokenTextField from './TokenTextField';
import { GENERATE_CERTIFICATE_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import TokenActions from './TokenActions';
import useGenerateCertificate from '../api/mutations/useGenerateCertificate';
import { TokenComponentProps } from './index';
import { showAlert } from '@silverfort/sf-components';
import UserIdentifierDropdown from './UserIdentifierDropdown';
import TokenRadioGroupField from './TokenRadioGroupField';

type DuplicateRequestHandlingMode = 'deny' | 'allow';

const DUPLICATE_REQUEST_HANDLING_OPTIONS = [
    { title: 'Deny', value: 'deny' },
    { title: 'Allow', value: 'allow' }
];

type DispatchFn = (action: UpdateAction | AddTenantAction | UpdateTenantAction) => void;

interface PublicKey {
    publicKey: string;
    tenantId: string;
    name: string;
    lastModified: number;
    duplicateRequestHandlingMode: DuplicateRequestHandlingMode;
}

interface UpdateAction {
    type: typeof actions.UPDATE_MICROSOFT_MFA_TOKEN;
    payload: {
        fieldId: string;
        value: string;
    };
}

interface AddTenantAction {
    type: typeof actions.ADD_MICROSOFT_TENANT;
    payload: PublicKey;
}

interface UpdateTenantAction {
    type: typeof actions.UPDATE_MICROSOFT_TENANT_NAME;
    payload: Pick<PublicKey, 'tenantId' | 'name' | 'duplicateRequestHandlingMode'>;
}

export interface MicrosoftFields {
    aadUpnField: Field;
    aadPublicKeys: Field;
}

type MicrosoftProps = TokenComponentProps<MicrosoftFields, DispatchFn>;

const Microsoft: FC<MicrosoftProps> = ({ fields, dispatch, onSave, onCancel, editTenantId }) => {
    const aadUpnField = fields[MICROSOFT_MFA_TOKEN_FIELDS.AAD_UPN_FIELD.ID];
    const [microsoftTenant, setMicrosoftTenant] = useState<{
        newTenantId: string;
        newTenantName: string;
        duplicateRequestHandlingMode: DuplicateRequestHandlingMode;
    }>({
        newTenantId: '',
        newTenantName: '',
        duplicateRequestHandlingMode: 'deny'
    });

    const isSomeFieldsEmpty = microsoftTenant.newTenantId === '' || microsoftTenant.newTenantName === '';

    const onGenerateSuccess = (data: string) => {
        dispatchAddTenant({
            publicKey: data,
            tenantId: microsoftTenant.newTenantId,
            name: microsoftTenant.newTenantName,
            lastModified: Date.now(),
            duplicateRequestHandlingMode: microsoftTenant.duplicateRequestHandlingMode
        });
        onSave();
    };

    const onGenerateError = () => {
        showAlert('Failed to generate certificate', 'error');
    };

    const { generateCertificateMutation, isLoading } = useGenerateCertificate({
        onSuccessCallback: onGenerateSuccess,
        onErrorCallback: onGenerateError
    });

    const dispatchUpdate = (fieldId: string, value: string) => {
        return dispatch({
            type: actions.UPDATE_MICROSOFT_MFA_TOKEN,
            payload: { fieldId, value }
        });
    };

    const dispatchAddTenant = (value: PublicKey) => {
        return dispatch({
            type: actions.ADD_MICROSOFT_TENANT,
            payload: value
        });
    };

    const dispatchUpdateTenantName = (
        tenantId: string,
        name: string,
        duplicateRequestHandlingMode: DuplicateRequestHandlingMode
    ) => {
        return dispatch({
            type: actions.UPDATE_MICROSOFT_TENANT_NAME,
            payload: { tenantId, name, duplicateRequestHandlingMode }
        });
    };

    const handleSave = () => {
        if (editTenantId) {
            dispatchUpdateTenantName(
                editTenantId,
                microsoftTenant.newTenantName,
                microsoftTenant.duplicateRequestHandlingMode
            );
            onSave();
            return;
        }

        generateCertificateMutation({
            certificateType: GENERATE_CERTIFICATE_TYPES.AAD,
            fields: { tenantID: microsoftTenant.newTenantId }
        });
    };

    useEffect(() => {
        if (editTenantId) {
            const tenant = fields[MICROSOFT_MFA_TOKEN_FIELDS.AAD_PUBLIC_KEY.ID].value.tenants.find(
                (tenant: PublicKey) => tenant.tenantId === editTenantId
            );
            if (tenant) {
                setMicrosoftTenant({
                    newTenantId: tenant.tenantId,
                    newTenantName: tenant.name,
                    duplicateRequestHandlingMode: tenant.duplicateRequestHandlingMode ?? 'deny'
                });
            }
        }
    }, [editTenantId, fields]);

    const aadUpnOptions = (aadUpnField.options || []).map((option) => ({
        title: option.label,
        value: option.value
    }));

    return (
        <>
            <UserIdentifierDropdown
                options={aadUpnOptions}
                selectedOption={aadUpnField.value}
                onChange={(val) => dispatchUpdate(aadUpnField.id, val)}
                mandatory
                label={aadUpnField.name}
            />

            <TokenTextField
                label="Tenant name"
                value={microsoftTenant.newTenantName}
                onChange={(val) => setMicrosoftTenant({ ...microsoftTenant, newTenantName: val })}
                isSecret={false}
            />
            <TokenTextField
                label="Tenant ID"
                value={microsoftTenant.newTenantId}
                onChange={(val) => setMicrosoftTenant({ ...microsoftTenant, newTenantId: val })}
                isSecret={false}
                disabled={editTenantId !== undefined}
            />

            <TokenRadioGroupField
                label="Duplicate Request Handling Mode"
                labelClassName="w-[245px]"
                options={DUPLICATE_REQUEST_HANDLING_OPTIONS}
                selectedOption={microsoftTenant.duplicateRequestHandlingMode}
                onChange={(val) =>
                    setMicrosoftTenant({
                        ...microsoftTenant,
                        duplicateRequestHandlingMode: val as DuplicateRequestHandlingMode
                    })
                }
                mandatory
                tooltipInfo="Duplicate requests may occur when multiple authentication attempts are triggered in a short time window. In some cases, the MFA provider may not process all requests reliably, which can lead to failed or incomplete MFA flows. This setting controls how duplicated authentication requests are handled in cases where MFA cannot be completed, determining whether access is allowed or denied based on the configured behavior."
            />
            <TokenActions
                disabled={isLoading}
                onSave={handleSave}
                onCancel={onCancel}
                saveDisabled={isSomeFieldsEmpty}
            />
        </>
    );
};

export default Microsoft;
